import type { MarketAccountDetailsMessageData } from '@src/containers/Admin/Counterparties/CounterpartyTypes';
import {
  getLpSettlementModeFromSettlement,
  LpSettlementMode,
  type LpSettlementMode as LpSettlementModeType,
} from '@src/containers/Admin/Counterparties/lpSettlementMode';
import type { TMarketAccountDetails } from '@src/containers/Admin/Counterparties/types';
import {
  LPSettlementBatchSourceEnum,
  LPSettlementInstructionStatusEnum,
  MixpanelEventProperty,
  SettlementInstructionDirectionEnum,
  type ModeEnum,
} from '@talos/kyoko';
import type { LPSettlementInstructionMessageData } from '../generated/lpSettlementTypes';

type SettlementFields = {
  SettlementMode?: ModeEnum;
  enableAutomaticBatching?: ModeEnum;
};

type MarketAccountDetailsLike = MarketAccountDetailsMessageData | TMarketAccountDetails;

export const LpSettlementBatchDetailsOpenedFrom = {
  GridMenu: 'grid-menu',
  RowDoubleClick: 'row-double-click',
  AfterCreate: 'after-create',
  AfterAddToBatch: 'after-add-to-batch',
  ErrorRetry: 'error-retry',
  DetailsButton: 'details-button',
} as const;

export type LpSettlementBatchDetailsOpenedFrom =
  (typeof LpSettlementBatchDetailsOpenedFrom)[keyof typeof LpSettlementBatchDetailsOpenedFrom];

function getSettlementFieldsFromDetails(details: MarketAccountDetailsLike | undefined): SettlementFields | undefined {
  if (!details) {
    return undefined;
  }

  if ('settlement' in details && details.settlement) {
    return details.settlement;
  }

  if ('SettlementMode' in details) {
    return {
      SettlementMode: details.SettlementMode,
      enableAutomaticBatching: details.enableAutomaticBatching,
    };
  }

  return undefined;
}

export function getLpSettlementModeFromMarketAccountDetails(
  details: MarketAccountDetailsLike | undefined
): LpSettlementModeType {
  return getLpSettlementModeFromSettlement(getSettlementFieldsFromDetails(details));
}

export function hasLpSettlementModeChanged(previousMode: LpSettlementModeType, newMode: LpSettlementModeType): boolean {
  return previousMode !== newMode;
}

function getBatchSourceMethod(source: unknown): 'Manual' | 'Auto' | undefined {
  switch (source) {
    case LPSettlementBatchSourceEnum.Manual:
      return 'Manual';
    case LPSettlementBatchSourceEnum.Automatic:
      return 'Auto';
    default:
      return undefined;
  }
}

export function buildBatchContextProperties(batch: {
  Status?: string;
  CreationSource?: unknown;
}): Record<string, unknown> {
  const method = getBatchSourceMethod(batch.CreationSource);
  return {
    ...(batch.Status ? { [MixpanelEventProperty.Status]: batch.Status } : {}),
    ...(method ? { [MixpanelEventProperty.Method]: method } : {}),
  };
}

export function buildBatchDetailsOpenedEventProperties({
  openedFrom,
  status,
  creationSource,
}: {
  openedFrom: LpSettlementBatchDetailsOpenedFrom;
  status?: string;
  creationSource?: LPSettlementBatchSourceEnum;
}): Record<string, unknown> {
  return {
    [MixpanelEventProperty.OpenedFrom]: openedFrom,
    ...buildBatchContextProperties({ Status: status, CreationSource: creationSource }),
  };
}

export function buildChangeLpSettlementModeProperties({
  previousMode,
  newMode,
  marketAccountType,
}: {
  previousMode: LpSettlementModeType;
  newMode: LpSettlementModeType;
  marketAccountType: string;
}): Record<string, unknown> | null {
  if (!hasLpSettlementModeChanged(previousMode, newMode)) {
    return null;
  }

  const lpSettlementEnabled = newMode === LpSettlementMode.Manual || newMode === LpSettlementMode.Auto;
  const autoBatchingEnabled = newMode === LpSettlementMode.Auto;

  return {
    [MixpanelEventProperty.Value]: newMode,
    [MixpanelEventProperty.Enabled]: lpSettlementEnabled,
    [MixpanelEventProperty.AutoBatchingEnabled]: autoBatchingEnabled,
    [MixpanelEventProperty.Type]: marketAccountType,
  };
}

export function isInstructionCompleted(status: LPSettlementInstructionStatusEnum | undefined): boolean {
  return status === LPSettlementInstructionStatusEnum.Completed;
}

export function isIncomingInstruction(
  direction: SettlementInstructionDirectionEnum | undefined
): direction is typeof SettlementInstructionDirectionEnum.Inbound {
  return direction === SettlementInstructionDirectionEnum.Inbound;
}

export function buildInstructionContextProperties(
  instruction: LPSettlementInstructionMessageData
): Record<string, unknown> {
  const method = getBatchSourceMethod(instruction.CompletionSource);
  return {
    ...(instruction.Direction ? { [MixpanelEventProperty.Direction]: instruction.Direction } : {}),
    ...(method != null ? { [MixpanelEventProperty.Method]: method } : {}),
  };
}

export function buildManualBatchCreatedEventProperties({
  reportCount,
  instructionCount,
}: {
  reportCount: number;
  instructionCount: number;
}): Record<string, unknown> {
  return {
    [MixpanelEventProperty.Method]: LPSettlementBatchSourceEnum.Manual,
    [MixpanelEventProperty.Count]: reportCount,
    [MixpanelEventProperty.InstructionCount]: instructionCount,
  };
}

export function buildTradesAddedToBatchEventProperties({
  reportCount,
}: {
  reportCount: number;
}): Record<string, unknown> {
  return {
    [MixpanelEventProperty.Count]: reportCount,
  };
}
